/**
 * Wave 10 zod-mandate — runtime validation entry points for
 * `@norkron/web-sync-queue`'s public surface.
 *
 * Every public function / class entry / hook validates its inputs on
 * invocation; invalid inputs are surfaced via `@norkron/web-errors` as
 * `WEB_SYNC_QUEUE_INVALID_INPUT` warnings. The code's severity is
 * registry-authoritative (web-errors@0.6.1) — declared `warning` in
 * `src/index.ts`'s inline `registerCodes`; call sites pass NO explicit
 * severity so it flows from the registry automatically.
 *
 * The functions never throw on invalid input — they emit a warning and let
 * the underlying call attempt the best-effort behaviour. Mirrors
 * `frontend/web/web-survey/src/validate-input.ts`.
 *
 * @example
 * ```ts
 * import { validateEnqueueInput } from './validate-input'
 * validateEnqueueInput(input)
 * ```
 */
import { reportInvalidInput } from './error-codes'
import { SYNC_QUEUE_LIB_TOKEN } from './error-codes'
import { BackoffAttemptSchema, BackoffOptionsSchema } from './__schemas__/Backoff.schema'
import { BatchResponseSchema } from './__schemas__/Confirmation.schema'
import { ResolveConflictPolicySpecSchema } from './__schemas__/ConflictPolicy.schema'
import {
	UseConfirmationsInputSchema,
	UseConflictsInputSchema,
	UseOutboxEntriesInputSchema,
	UseSyncStatusInputSchema,
} from './__schemas__/Hooks.schema'
import { CreateOfflineStoreCollectionArgsSchema } from './__schemas__/OfflineStore.schema'
import { EnqueueInputSchema } from './__schemas__/OutboxEntry.schema'
import { RegisterBackgroundSyncSchema } from './__schemas__/ServiceWorker.schema'
import { SyncQueueConfigSchema } from './__schemas__/SyncQueueConfig.schema'
import { IsUuidV7InputSchema, UuidV7TimestampSchema } from './__schemas__/Uuid.schema'
import { toZodLikeSafeParseError } from './zod-bridge'

const LIB = '@norkron/web-sync-queue'

export function validateSyncQueueConfig(config: unknown): void {
	const parsed = SyncQueueConfigSchema.safeParse(config)
	if (!parsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'SyncQueue',
			parseResult: toZodLikeSafeParseError(parsed),
		})
	}
}

export function validateEnqueueInput(input: unknown): void {
	const parsed = EnqueueInputSchema.safeParse(input)
	if (!parsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'enqueue',
			parseResult: toZodLikeSafeParseError(parsed),
		})
	}
}

export function validateResolveConflictArgs(
	clientEventUuid: unknown,
	decision: unknown,
): void {
	const parsed = ResolveConflictArgsSchema.safeParse({ clientEventUuid, decision })
	if (!parsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'resolveConflict',
			parseResult: toZodLikeSafeParseError(parsed),
		})
	}
}

export function validateResolveConflictPolicySpec(spec: unknown): void {
	const parsed = ResolveConflictPolicySpecSchema.safeParse(spec)
	if (!parsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'resolveConflictPolicy',
			parseResult: toZodLikeSafeParseError(parsed),
		})
	}
}

export function validateNextBackoffMsArgs(
	attempt: unknown,
	opts: unknown,
): void {
	const attemptParsed = BackoffAttemptSchema.safeParse(attempt)
	if (!attemptParsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'nextBackoffMs',
			parseResult: toZodLikeSafeParseError(attemptParsed),
		})
		return
	}
	const optsParsed = BackoffOptionsSchema.safeParse(opts)
	if (!optsParsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'nextBackoffMs',
			parseResult: toZodLikeSafeParseError(optsParsed),
		})
	}
}

export function validateUuidv7Timestamp(now: unknown): void {
	const parsed = UuidV7TimestampSchema.safeParse(now)
	if (!parsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'uuidv7',
			parseResult: toZodLikeSafeParseError(parsed),
		})
	}
}

export function validateUuidv7TimestampString(uuid: unknown): void {
	const parsed = Uuidv7TimestampStringSchema.safeParse(uuid)
	if (!parsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'uuidv7Timestamp',
			parseResult: toZodLikeSafeParseError(parsed),
		})
	}
}

export function validateIsUuidv7Input(value: unknown): void {
	// `isUuidv7` accepts arbitrary input by contract — the schema is a
	// pass-through. Kept for symmetry + uniform validator surface.
	const parsed = IsUuidV7InputSchema.safeParse(value)
	if (!parsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'isUuidv7',
			parseResult: toZodLikeSafeParseError(parsed),
		})
	}
}

export function validateRegisterBackgroundSyncArgs(
	serviceWorkerUrl: unknown,
	tag: unknown,
): void {
	const parsed = RegisterBackgroundSyncSchema.safeParse({ serviceWorkerUrl, tag })
	if (!parsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'registerBackgroundSync',
			parseResult: toZodLikeSafeParseError(parsed),
		})
	}
}

export function validateUseSyncStatusInput(
	queue: unknown,
	refreshMs: unknown,
): void {
	const parsed = UseSyncStatusInputSchema.safeParse(
		refreshMs === undefined ? { queue } : { queue, refreshMs },
	)
	if (!parsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'useSyncStatus',
			parseResult: toZodLikeSafeParseError(parsed),
		})
	}
}

export function validateUseConflictsInput(queue: unknown): void {
	const parsed = UseConflictsInputSchema.safeParse({ queue })
	if (!parsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'useConflicts',
			parseResult: toZodLikeSafeParseError(parsed),
		})
	}
}

export function validateUseConfirmationsInput(
	queue: unknown,
	onConfirmation: unknown,
): void {
	const parsed = UseConfirmationsInputSchema.safeParse({
		queue,
		onConfirmation,
	})
	if (!parsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'useConfirmations',
			parseResult: toZodLikeSafeParseError(parsed),
		})
	}
}

export function validateUseOutboxEntriesInput(
	queue: unknown,
	refreshMs: unknown,
	limit: unknown,
): void {
	const input: Record<string, unknown> = { queue }
	if (refreshMs !== undefined) input.refreshMs = refreshMs
	if (limit !== undefined) input.limit = limit
	const parsed = UseOutboxEntriesInputSchema.safeParse(input)
	if (!parsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'useOutboxEntries',
			parseResult: toZodLikeSafeParseError(parsed),
		})
	}
}

/**
 * Validate what a custom `SyncQueueConfig.transport` handed back. Unlike every
 * other validator here this guards an INBOUND value from consumer code rather
 * than a call argument — a transport is the one seam where a consumer supplies
 * the engine with a wire-shaped object.
 *
 * Warn-only, like the rest: `applyResults` is already total over a malformed
 * response (an unknown `client_event_uuid` is skipped; an entry the transport
 * omitted is re-pended and safely replayed under its idempotency key), so the
 * warning tells the consumer their sender is off-contract without changing what
 * the drain does.
 */
export function validateTransportResponse(response: unknown): void {
	const parsed = BatchResponseSchema.safeParse(response)
	if (!parsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'transport',
			parseResult: toZodLikeSafeParseError(parsed),
		})
	}
}

/**
 * Validate the `@norkron/web-sync-queue/offline-store` factory arguments. Lives
 * here rather than in the subpath module so every public boundary in the package
 * validates through one file, and so the subpath inherits the same warn-only
 * (never-throw) convention.
 */
export function validateCreateOfflineStoreCollectionArgs(
	dbName: unknown,
	tableName: unknown,
	opts: unknown,
): void {
	const input: Record<string, unknown> = { dbName, tableName }
	if (opts !== undefined) input.opts = opts
	const parsed = CreateOfflineStoreCollectionArgsSchema.safeParse(input)
	if (!parsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'createOfflineStoreCollection',
			parseResult: toZodLikeSafeParseError(parsed),
		})
	}
}

export function validateListPendingArgs(limit: unknown): void {
	const parsed = ListPendingArgsSchema.safeParse(
		limit === undefined ? {} : { limit },
	)
	if (!parsed.success) {
		reportInvalidInput({
			lib: LIB,
			libToken: SYNC_QUEUE_LIB_TOKEN,
			fn: 'listPending',
			parseResult: toZodLikeSafeParseError(parsed),
		})
	}
}

// Local-to-validator helpers — kept inline so the public schemas barrel
// stays focused on per-export shapes rather than validator plumbing.
import { z } from 'zod'

const ResolveConflictArgsSchema = z
	.object({
		clientEventUuid: z.string().min(1),
		decision: z.enum(['keep', 'discard']),
	})
	.strict()

const Uuidv7TimestampStringSchema = z.string().min(1)

const ListPendingArgsSchema = z
	.object({
		limit: z.number().int().nonnegative().optional(),
	})
	.strict()
